import ls from '@/storage/index'
import { router } from '@/route/index.js'
import eventBus from '@/utils/eventBus.js'
import { iconMap } from '@/utils/icons.js'
import { getRequest } from '@/utils/request.js'
import { notification } from 'ant-design-vue'
import { defineStore } from 'pinia'
import { h, reactive } from 'vue'

const usePermissionStore = defineStore('permission', () => {
  const state = reactive({
    initialized: false,
    // 权限列表
    permsList: [],
    // 菜单列表
    menuList: [],
    routerList: [],
  })

  const resetPermissionState = () => {
    state.permsList = []
    state.menuList = []
    state.routerList = []
  }

  const isAuthError = (error) => error?.response?.status === 401

  const handleAuthError = () => {
    resetPermissionState()
    ls.remove('token')
    ls.remove('refreshToken')
    eventBus.emit('request:401')
  }

  const handleInitializeError = (error, description) => {
    if (isAuthError(error)) {
      handleAuthError()
      return
    }
    notification.error({
      message: '系统提示',
      description,
    })
  }

  const loadPermsList = async () => {
    try {
      await fetchPermsListAsync()
    } catch (error) {
      if (isAuthError(error)) {
        throw error
      }
      state.permsList = []
      notification.warning({
        message: '系统提示',
        description: '按钮权限加载失败，已暂时隐藏权限按钮',
      })
    }
  }

  const loadMenuAndRouterList = async () => {
    try {
      await fetchMenuAndRouterListAsync()
    } catch (error) {
      state.menuList = []
      state.routerList = []
      throw error
    }
  }

  const handleRouteInitializeError = (error) => {
    if (error?.response?.status === 401) {
      handleAuthError()
      return
    }
    handleInitializeError(error, '菜单路由加载失败，请检查后端服务')
  }

  /**
   * 初始化权限、菜单、路由
   * @returns {Promise<void>}
   */
  const initialize = async () => {
    // 直接从 storage 获取 token，避免循环依赖
    const token = ls.get('token')
    if (token) {
      try {
        await loadPermsList()
        await loadMenuAndRouterList()
      } catch (error) {
        handleRouteInitializeError(error)
      }
    }
    // 通知布局刷新菜单
    eventBus.emit('menu:ready')
  }

  /**
   * 获取用户权限
   * @returns {Promise<void>}
   */
  const fetchPermsListAsync = async () => {
    const response = await getRequest('/getLoginUserPerms')
    if (response.success) {
      state.permsList = response.data
      console.info('state.permsList', state.permsList)
      console.info('response.data', response.data)
    }
  }

  /**
   * 获取菜单以及路由
   * @returns {Promise<void>}
   */
  const fetchMenuAndRouterListAsync = async () => {
    // 获取后端菜单并注册动态路由
    const response = await getRequest('/getLoginUserPermission')
    if (response.success) {
      const allMenuPermissionList = response.data.filter((permission) => {
        return permission.type === 0 || permission.type === 1
      })
      generateMenuList(allMenuPermissionList)
      generateRouteList(allMenuPermissionList)
      state.routerList.forEach((routeItem) => {
        router.addRoute(routeItem)
      })
    }
  }

  /**
   * 构建菜单列表
   * @param permissionList
   */
  const generateMenuList = (permissionList) => {
    const allMenuPermissionList = permissionList
      .filter((permission) => !permission.hidden)
      .map((permission) => {
        const menuItem = {
          parentId: permission.parentId,
          key: permission.id,
          title: permission.name,
          label: permission.name,
          path: permission.path,
          componentName: permission.component,
          type: permission.type,
          keepAlive: permission.keepAlive,
        }
        if (permission.icon && iconMap[permission.icon]) {
          // 存储图标名称，用于序列化和 personalHomeStore
          menuItem.iconName = permission.icon
          // 存储图标 VNode，用于 Ant Design Menu 渲染
          menuItem.icon = h(iconMap[permission.icon])
        }
        return menuItem
      })

    const rootMenuList = allMenuPermissionList.filter((menu) => {
      return menu.type === 0
    })
    rootMenuList.forEach((rootMenu) => {
      generateChildrenMenuList(rootMenu, allMenuPermissionList)
    })

    state.menuList = rootMenuList
  }

  /**
   * 构建子菜单
   * @param parentPermission
   * @param permissionList
   */
  const generateChildrenMenuList = (parentPermission, permissionList) => {
    const parentId = parentPermission.key
    const childrenMenuList = permissionList.filter((permission) => {
      return permission.parentId === parentId
    })
    if (childrenMenuList.length) {
      parentPermission.children = childrenMenuList
      childrenMenuList.forEach((permission) => {
        generateChildrenMenuList(permission, permissionList)
      })
    }
  }

  const generateRouteList = (permissionList) => {
    const modules = import.meta.glob(['/src/views/**/*.vue', '!/src/views/**/modules/**/*.vue'])
    const allRouteList = permissionList.map((permission) => {
      const component = `/src/views${permission.component.startsWith('/') ? permission.component : '/' + permission.component}.vue`
      const indexComponent = component.replace(/\.vue$/, '/index.vue')
      let routeItem = {
        name: permission.componentName,
        path: permission.path,
        component: modules[/* @vite-ignore */ component] || modules[/* @vite-ignore */ indexComponent],
        meta: {
          id: permission.id,
          componentName: permission.componentName,
          parentId: permission.parentId,
          type: permission.type,
          keepAlive: permission.keepAlive,
          name: permission.name,
        },
      }

      if (permission.component === 'layout' || permission.component === '/layout/Layout') {
        routeItem.component = () => import('/src/layout/Layout.vue')
      }

      if (
        permission.component === 'secondLayout' ||
        permission.component === '/layout/SecondLayout'
      ) {
        routeItem.component = () => import('/src/layout/SecondLayout.vue')
      }

      if (
        permission.type === 0 &&
        permission.component !== 'layout' &&
        permission.component !== '/layout/Layout'
      ) {
        routeItem = {
          name: permission.componentName + 'Parent',
          path: permission.path,
          component: () => import('/src/layout/Layout.vue'),
          children: [{ ...routeItem }],
          meta: {
            id: permission.id + '_parent',
            componentName: permission.componentName + 'Parent',
            parentId: permission.parentId + '_parent',
            type: permission.type,
          },
        }
      }
      return routeItem
    })

    const rootRouteList = allRouteList.filter((routeItem) => {
      return routeItem.meta.type === 0
    })

    rootRouteList.forEach((rootRoute) => {
      generateChildrenRouteList(rootRoute, allRouteList)
    })
    state.routerList = rootRouteList
  }

  const generateChildrenRouteList = (parentRoute, routeList) => {
    const parentId = parentRoute.meta.id
    const childrenRouteList = routeList.filter((routeItem) => {
      return routeItem.meta.parentId === parentId
    })
    if (childrenRouteList.length) {
      parentRoute.children = childrenRouteList
      childrenRouteList.forEach((childrenRouteItem) => {
        generateChildrenRouteList(childrenRouteItem, routeList)
      })
    }
  }

  const setPermsList = (permsList) => {
    state.permsList = permsList
  }

  const setMenuList = (menuList) => {
    state.menuList = menuList
  }

  const hasPermission = (requiredPerm) => {
    return state.permsList.includes(requiredPerm)
  }

  return {
    state,
    setPermsList,
    setMenuList,
    initialize,
    fetchPermsListAsync,
    fetchMenuAndRouterListAsync,
    hasPermission,
  }
})

export default usePermissionStore
